import { SESSIONS_TEST_IDS } from '@/components/admin/sessions/contracts';
import { sessionStatusContext } from '@/components/admin/sessions/sessions-table';
import {
  VersionedDialog,
  VersionedDialogSection,
} from '@/components/admin/versioned/versioned-dialog';
import { Callout } from '@/components/ui/callout';
import {
  INTERRUPT_LINK_LABEL,
  type InterruptionRefusal,
  lastActivityAt,
  relativeAgo,
} from '@/lib/session-interruption';
import { sessionDate, sessionTime } from '@/lib/session-labels';
import type { SessionRow } from '@matter_extractor/shared';
import { useState } from 'react';

/**
 * « Interrompre cette session ? », au patron exact de `session-delete-dialog.tsx`
 * (issue #199, interruption.md §4.2 ; maquette `interruption.html`, section 3).
 *
 * ## Elle DIT la dernière activité, et laisse l'opérateur juger
 *
 * Aucun seuil d'inactivité (D3) : une session d'essai qu'on vient de lancer
 * s'interrompt tout de suite, une session qui parle encore se voit à son « à
 * l'instant ». La boîte dit donc QUAND la dernière trace a été écrite - la
 * durée relative et l'heure - et OÙ la session en était, avec les mots que la
 * liste emploie sous la pastille (`sessionStatusContext`), pas une seconde
 * façon de dire une position.
 *
 * La dernière trace est `startedAt + durationMs` : la durée d'une ligne court
 * du départ à la dernière chose qui s'est réellement passée, jamais jusqu'à
 * `now` (issue #199, H8).
 *
 * ## Le focus sur « Annuler », le ton n'est PAS `destructive`
 *
 * `VersionedDialog` est réutilisé : c'est lui qui met le focus sur Annuler, et
 * une frappe d'Entrée ne coupe jamais personne. Rien n'est effacé, donc le
 * bouton reste bleu ; ce qui ne se défait pas est dit par l'encart neutre.
 *
 * ## Un refus garde la boîte ouverte
 *
 * Le refus est un encart rouge dans la boîte, avec sa phrase par `code`
 * (`lib/session-interruption.ts`). Quand il décrit un ÉTAT de la session
 * (déjà finie, en corbeille...) et non un moment (`turn-in-flight`), le bouton
 * de confirmation s'efface et « Annuler » devient « Fermer » : le renvoyer ne
 * changerait rien.
 *
 * Les DEUX écrans (parcours et conversation) montent ce composant-ci, par
 * `ReviewEntryButton`.
 */

export interface SessionInterruptDialogProps {
  readonly row: SessionRow;
  readonly busy: boolean;
  /** Le refus du serveur, déjà mis en mots. Null quand il n'y en a pas. */
  readonly refusal: InterruptionRefusal | null;
  /** L'instant de référence de « il y a... » ; l'horloge au montage par défaut. */
  readonly now?: Date;
  readonly onConfirm: () => void;
  readonly onCancel: () => void;
}

export function SessionInterruptDialog({
  row,
  busy,
  refusal,
  now,
  onConfirm,
  onCancel,
}: SessionInterruptDialogProps) {
  // Fixé au montage : « il y a 2 h 14 » ne doit pas défiler sous les yeux de qui le lit.
  const [reference] = useState(() => now ?? new Date());
  const name = row.experienceName ?? 'Expérience retirée';
  const last = lastActivityAt(row);
  const ago = relativeAgo(last, reference);
  const where = sessionStatusContext(row);
  const final = refusal?.final === true;

  return (
    <VersionedDialog
      data-testid={SESSIONS_TEST_IDS.interruptDialog}
      title="Interrompre cette session ?"
      description={`« ${name} », commencée le ${sessionDate(row.startedAt)}.`}
      confirmLabel={INTERRUPT_LINK_LABEL}
      confirmHidden={final}
      cancelLabel={final ? 'Fermer' : 'Annuler'}
      busy={busy}
      error={null}
      onConfirm={onConfirm}
      onCancel={onCancel}
    >
      <VersionedDialogSection title="Dernière activité">
        <dl className="grid grid-cols-[auto_minmax(0,1fr)] items-baseline gap-x-3 gap-y-0.5 text-gray-700 text-sm">
          <dt className="text-gray-500 text-xs">quand</dt>
          <dd data-testid={SESSIONS_TEST_IDS.interruptWhen} data-ago={ago} className="m-0">
            <b className="font-semibold text-gray-900">{ago}</b> ({sessionTime(last.toISOString())})
          </dd>
          {where === null ? null : (
            <>
              <dt className="text-gray-500 text-xs">où</dt>
              <dd data-testid={SESSIONS_TEST_IDS.interruptWhere} className="m-0">
                {where}
              </dd>
            </>
          )}
        </dl>
      </VersionedDialogSection>

      {refusal === null ? (
        <>
          <VersionedDialogSection title="Ce qui se passe">
            <p className="text-gray-700 text-sm">
              La session passe au statut <strong>interrompue</strong>, à votre nom. La relecture
              s’ouvre aussitôt.
            </p>
            <p className="text-gray-700 text-sm">
              La personne ne peut plus continuer. Si elle revient, elle lit que la conversation est
              terminée et peut en recommencer une.
            </p>
          </VersionedDialogSection>

          <Callout tone="neutral" title="Ce geste ne se défait pas.">
            Rien n’est lancé : ni rangement, ni détermination du noyau. Les essais à blanc restent
            possibles sur form.dev.
          </Callout>
        </>
      ) : (
        <Callout tone="danger" live="alert" data-testid={SESSIONS_TEST_IDS.interruptRefusal}>
          <span data-code={refusal.code ?? 'none'}>{refusal.sentence}</span>
        </Callout>
      )}
    </VersionedDialog>
  );
}
